import { useChatFilterContext } from "contexts/ChatFilterContext";
import { usePaginatedChannels } from "hooks/usePaginatedChannels";
import { useContext, useEffect, useMemo } from "react";
import { useChatContext } from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { Channel, ChannelFilters, ChannelSort } from "stream-chat";
import { ChannelSearchList } from "components/ChannelSearchList";
import { EmptyChannelList } from "components/EmptyChannelList";
import Loader from "components/DesignSystem/Loader/loader";
import { filterExcludedChannels } from "util/isChannelLastMessageExcluded";
import { ChatFilterAccordion } from "./ChatFilterAccordion";
import { SelectedChannelsContext } from "./SelectedChannelContext";

export const UnrepliedView = ({
  filters,
  sort,
  onSelectCallback,
  unreplied,
  withWrapper = true,
  spocUnavailableGroups,
  onChannelUpdated,
}: {
  filters: ChannelFilters<DefaultStreamChatGenerics>;
  sort: ChannelSort<DefaultStreamChatGenerics>;
  onSelectCallback: (channelId?: string, messageId?: string) => void;
  unreplied: boolean;
  withWrapper?: boolean;
  spocUnavailableGroups?: string[];
  onChannelUpdated: (
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
    >
  ) => any;
}) => {
  const { client, channelsQueryState } = useChatContext();

  const channelFilters = spocUnavailableGroups
    ? {
        ...filters,
        group_uuid: { $nin: spocUnavailableGroups },
      }
    : filters;

  const { channels, hasNextPage, loadNextPage } = usePaginatedChannels(
    client,
    channelFilters,
    sort,
    { limit: 15 },
    () => {},
    5000,
    undefined,
    onChannelUpdated
  );

  const {
    filters: { EXCLUDE: excludeOptions },
  } = useChatFilterContext();
  const visibleChannels = useMemo(
    () => filterExcludedChannels(channels, excludeOptions),
    [channels, excludeOptions]
  );

  const { setChannelsForSource } = useContext(SelectedChannelsContext);
  const channelInfos = visibleChannels
    .filter((c) => Boolean(c?.id))
    .map((c) => ({
      id: c.id as string,
      needsReply: Boolean(c.data?.needs_reply),
    }));
  const channelInfosKey = channelInfos
    .map((c) => `${c.id}:${c.needsReply ? 1 : 0}`)
    .join(",");

  useEffect(() => {
    setChannelsForSource("unreplied", channelInfos);
  }, [channelInfosKey]);

  useEffect(
    () => () => setChannelsForSource("unreplied", []),
    [setChannelsForSource]
  );

  if (channelsQueryState.queryInProgress === "reload") {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!withWrapper) {
    if (visibleChannels.length === 0) {
      return <EmptyChannelList />;
    }

    return (
      <ChannelSearchList
        searchValue=""
        onSelect={onSelectCallback}
        unreplied={unreplied}
        windowElementId="unrepliedScrollParent"
        channels={visibleChannels}
        hasNextPage={hasNextPage}
        loadNextPage={loadNextPage}
      />
    );
  }

  if (visibleChannels.length === 0) {
    return (
      <ChatFilterAccordion
        title="Unreplied"
        contentId="unrepliedScrollParent"
        isEmpty={true}
      >
        <EmptyChannelList />
      </ChatFilterAccordion>
    );
  }

  return (
    <ChatFilterAccordion
      title="Unreplied"
      contentId="unrepliedScrollParent"
      isEmpty={false}
      takesFullHeight={visibleChannels.length >= 15}
    >
      <ChannelSearchList
        searchValue=""
        onSelect={onSelectCallback}
        unreplied={unreplied}
        windowElementId="unrepliedScrollParent"
        channels={visibleChannels}
        hasNextPage={hasNextPage}
        loadNextPage={loadNextPage}
      />
    </ChatFilterAccordion>
  );
};
